<template>
  <div>
    <!-- Mobile backdrop -->
    <transition name="gnm-bd-fade">
      <div v-if="open" class="gnm-bd" @click="$emit('close')"></div>
    </transition>

    <!-- Sidebar -->
    <nav class="gnm-nav" :class="{ 'gnm-nav--collapsed': collapsed, 'gnm-nav--open': open }">

      <!-- ── Brand header ── -->
      <div class="gnm-head">
        <div class="gnm-brand" v-if="!collapsed">
          <div class="gnm-brand-icon">
            <svg viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5z" fill="#fff" opacity=".9"/><path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </div>
          <div class="gnm-brand-text">GNM<span>CRM</span></div>
        </div>
        <div v-else class="gnm-brand-icon gnm-brand-icon--solo">
          <svg viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5z" fill="#fff" opacity=".9"/><path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </div>
        <button class="gnm-col-btn" @click="toggleCollapse" :title="collapsed ? 'Expand' : 'Collapse'">
          <svg v-if="!collapsed" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>

      <!-- ── User profile strip ── -->
      <router-link to="/profile1" class="gnm-user" :class="{ 'gnm-user--slim': collapsed }" @click.native="onNavClick" :title="collapsed ? gnmUserName + ' — View profile' : 'View profile'">
        <div class="gnm-av">
          {{ gnmInitials }}
          <span class="gnm-av-dot" :class="{ on: gnmOnline }"></span>
        </div>
        <template v-if="!collapsed">
          <div class="gnm-uinfo">
            <div class="gnm-uname">{{ gnmUserName }}</div>
            <div class="gnm-urole">{{ gnmRoleLabel }}</div>
          </div>
          <button class="gnm-online-btn" :class="{ on: gnmOnline }" @click.prevent.stop="gnmOnline = !gnmOnline" :title="gnmOnline ? 'Set offline' : 'Set online'">
            <span class="gnm-online-knob"></span>
          </button>
        </template>
        <!-- Logout shortcut always visible in user strip -->
        <button class="gnm-user-logout" @click.prevent.stop="doLogout" title="Log out">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </button>
      </router-link>

      <!-- ── Timezone row ── -->
      <div class="gnm-tz-bar" v-if="!collapsed">
        <div class="gnm-tz-cell">
          <span class="gnm-tz-flag">🇬🇧</span>
          <span class="gnm-tz-time">{{ tzUK }}</span>
        </div>
        <div class="gnm-tz-sep"></div>
        <div class="gnm-tz-cell">
          <span class="gnm-tz-flag">🇮🇱</span>
          <span class="gnm-tz-time">{{ tzIL }}</span>
        </div>
        <div class="gnm-tz-sep"></div>
        <div class="gnm-tz-cell">
          <span class="gnm-tz-flag">🏝</span>
          <span class="gnm-tz-time">{{ tzCB }}</span>
        </div>
      </div>

      <!-- ── Scrollable nav ── -->
      <div class="gnm-scroll">
        <template v-for="(section, si) in navSections">
          <div class="gnm-sec" v-if="!collapsed" :key="'sl-' + si">
            <span class="gnm-sec-line"></span>
            <span class="gnm-sec-lbl">{{ section.label }}</span>
            <span class="gnm-sec-line"></span>
          </div>
          <router-link
            v-for="item in section.items"
            :key="item.to"
            :to="item.to"
            class="gnm-item"
            :class="['gnm-c-' + item.color, { 'gnm-item--active': isActive(item.to) }]"
            :title="collapsed ? item.label : ''"
            @click.native="onNavClick"
          >
            <span class="gnm-ico" v-html="item.icon"></span>
            <span class="gnm-lbl" v-if="!collapsed">{{ item.label }}</span>
            <span v-if="!collapsed && item.badge" class="gnm-badge">{{ item.badge }}</span>
            <span v-if="isActive(item.to)" class="gnm-pill"></span>
          </router-link>
        </template>

        <!-- Divider -->
        <div class="gnm-hr"></div>

        <!-- Logout -->
        <div class="gnm-item gnm-item--logout" @click="doLogout" :title="collapsed ? 'Log out' : ''">
          <span class="gnm-ico">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </span>
          <span class="gnm-lbl" v-if="!collapsed">Log out</span>
        </div>

        <!-- Mobile close -->
        <div class="gnm-item gnm-close-btn" @click="$emit('close')" v-if="open">
          <span class="gnm-ico">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </span>
          <span class="gnm-lbl" v-if="!collapsed">Close menu</span>
        </div>
      </div>

    </nav>
  </div>
</template>

<script>
const ROLE_MAP = {
  1: 'Admin', 2: 'Team Leader', 3: 'Manager', 4: 'Senior Agent',
  5: 'Agent', 6: 'Junior Agent', 7: 'New Agent', 8: 'Support',
  9: 'Retention', 10: 'Finance', 12: 'Super Admin',
};

const I = {
  clients:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
  tasks:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>',
  stats:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>',
  chat:     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
  comms:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.97a16 16 0 0 0 6.29 6.29l1.06-1.13a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>',
  aff:      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
  finance:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>',
  shifts:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
  games:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M6 12h4M8 10v4M15 12h2M18 12h2" stroke-linecap="round"/></svg>',
  users:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>',
  brokers:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-4 0v2"/><path d="M12 12v4M10 14h4"/></svg>',
  security: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
  quiz:     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17" stroke-width="2.5"/></svg>',
  instruct: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>',
  profile:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
  settings: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>',
};

export default {
  name: 'GnmNav',
  props: {
    open: { type: Boolean, default: false },
  },
  data() {
    const saved = localStorage.getItem('gnm-nav-collapsed');
    return {
      collapsed: saved === 'true',
      gnmOnline: true,
      tzTick: null,
      tzUK: '--:--',
      tzIL: '--:--',
      tzCB: '--:--',
    };
  },
  computed: {
    gnmUser() {
      return (this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user)
          || (this.$store.state.auth && this.$store.state.auth.user)
          || {};
    },
    gnmInitials() {
      const u = this.gnmUser;
      return (((u.first_name || '')[0] || '') + ((u.last_name || '')[0] || '')).toUpperCase() || 'GN';
    },
    gnmUserName() {
      const u = this.gnmUser;
      return ((u.first_name || '') + ' ' + (u.last_name || '')).trim() || 'User';
    },
    gnmRoleLabel() {
      return ROLE_MAP[this.gnmUser.role] || 'Agent';
    },
    navSections() {
      return [
        {
          key: 'crm', label: 'CRM',
          items: [
            { to: '/clients1',        label: 'Clients',      icon: I.clients,  color: 'dp' },
            { to: '/tasks1',          label: 'Tasks',        icon: I.tasks,    color: 'dp' },
            { to: '/stats1',          label: 'Statistics',   icon: I.stats,    color: 'dp' },
            { to: '/chat1',           label: 'Chat',         icon: I.chat,     color: 'dp' },
            { to: '/communications1', label: 'Comms',        icon: I.comms,    color: 'dp' },
            { to: '/affiliation1',    label: 'Affiliation',  icon: I.aff,      color: 'dp' },
          ],
        },
        {
          key: 'sales', label: 'Sales',
          items: [
            { to: '/finance1', label: 'Finance', icon: I.finance, color: 'sc' },
            { to: '/shifts1',  label: 'Shifts',  icon: I.shifts,  color: 'sc' },
            { to: '/games1',   label: 'Games',   icon: I.games,   color: 'sc' },
          ],
        },
        {
          key: 'team', label: 'Team',
          items: [
            { to: '/users1',    label: 'Users',    icon: I.users,    color: 'rf' },
            { to: '/brokers1',  label: 'Brokers',  icon: I.brokers,  color: 'rf' },
            { to: '/security1', label: 'Security', icon: I.security, color: 'rf' },
          ],
        },
        {
          key: 'admin', label: 'Admin',
          items: [
            { to: '/quiz1',        label: 'Quiz',         icon: I.quiz,     color: 'bn' },
            { to: '/instruction1', label: 'Instructions', icon: I.instruct, color: 'bn' },
            { to: '/settings1',    label: 'Settings',     icon: I.settings, color: 'bn' },
            { to: '/profile1',     label: 'My Profile',   icon: I.profile,  color: 'dp' },
          ],
        },
      ];
    },
  },
  watch: {
    collapsed(val) {
      localStorage.setItem('gnm-nav-collapsed', val);
      this.setSidebarWidth(val ? '60px' : '220px');
    },
  },
  mounted() {
    this.setSidebarWidth(this.collapsed ? '60px' : '220px');
    this.tickTz();
    this.tzTick = setInterval(this.tickTz, 30000);
  },
  beforeDestroy() {
    clearInterval(this.tzTick);
  },
  methods: {
    toggleCollapse() {
      this.collapsed = !this.collapsed;
    },
    setSidebarWidth(w) {
      document.documentElement.style.setProperty('--gnm-sw', w);
    },
    tickTz() {
      this.tzUK = this.tz('Europe/London');
      this.tzIL = this.tz('Asia/Jerusalem');
      this.tzCB = this.tz('America/Cayman');
    },
    tz(zone) {
      try {
        return new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: zone });
      } catch(e) { return '--:--'; }
    },
    isActive(path) {
      return this.$route && this.$route.path === path;
    },
    onNavClick() {
      this.$emit('close');
    },
    doLogout() {
      this.$store.dispatch('user/logOut');
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════
   GnmNav — Professional CRM Sidebar
   Desktop: fixed left, 220px expanded / 60px collapsed
   Mobile: slide-in overlay from left
   ═══════════════════════════════════════════════════ */

/* ── Backdrop ── */
.gnm-bd {
  position: fixed; inset: 0; z-index: 799;
  background: rgba(15,10,30,.4);
  backdrop-filter: blur(2px);
}
.gnm-bd-fade-enter-active, .gnm-bd-fade-leave-active { transition: opacity .22s; }
.gnm-bd-fade-enter, .gnm-bd-fade-leave-to { opacity: 0; }

/* ── Sidebar shell ── */
.gnm-nav {
  position: fixed;
  top: 0; left: 0; bottom: 0;
  width: 220px;
  background: #fff;
  border-right: 1px solid #e4e3df;
  display: flex; flex-direction: column;
  z-index: 800;
  transition: width .22s cubic-bezier(.4,0,.2,1), transform .22s cubic-bezier(.4,0,.2,1);
  overflow: hidden;
}
.gnm-nav--collapsed { width: 60px; }

@media (max-width: 767px) {
  .gnm-nav {
    width: 280px;
    transform: translateX(-100%);
    box-shadow: none;
  }
  .gnm-nav--open {
    transform: translateX(0);
    box-shadow: 4px 0 24px rgba(0,0,0,.1);
  }
  .gnm-nav--collapsed { width: 280px; transform: translateX(-100%); }
  .gnm-nav--collapsed.gnm-nav--open { transform: translateX(0); }
}

/* ── Brand header ── */
.gnm-head {
  display: flex; align-items: center; justify-content: space-between;
  height: 56px; padding: 0 14px; flex-shrink: 0;
  border-bottom: 1px solid #eeedea;
}
.gnm-brand {
  display: flex; align-items: center; gap: 10px;
}
.gnm-brand-icon {
  width: 28px; height: 28px; border-radius: 7px;
  background: linear-gradient(135deg, #7c5cfc 0%, #9d7ff8 100%);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(124,92,252,.35);
}
.gnm-brand-icon svg { width: 15px; height: 15px; }
.gnm-brand-icon--solo {
  width: 30px; height: 30px; border-radius: 8px;
  background: linear-gradient(135deg, #7c5cfc 0%, #9d7ff8 100%);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 6px rgba(124,92,252,.35);
  margin: 0 auto;
}
.gnm-brand-icon--solo svg { width: 15px; height: 15px; }
.gnm-brand-text {
  font-size: 15px; font-weight: 800; color: #1a1a1a;
  letter-spacing: -.5px; line-height: 1;
  white-space: nowrap;
}
.gnm-brand-text span { color: #7c5cfc; font-weight: 500; }
.gnm-col-btn {
  width: 28px; height: 28px; border: none; background: transparent;
  cursor: pointer; border-radius: 6px; color: #a09e98;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  transition: background .12s, color .12s;
}
.gnm-col-btn:hover { background: rgba(0,0,0,.05); color: #1a1a1a; }
.gnm-col-btn svg { width: 15px; height: 15px; }
@media (max-width: 767px) { .gnm-col-btn { display: none; } }

/* ── User profile ── */
.gnm-user {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px; flex-shrink: 0;
  border-bottom: 1px solid #eeedea;
  text-decoration: none; cursor: pointer;
  transition: background .12s;
}
.gnm-user:hover { background: rgba(124,92,252,.05); }
.gnm-user--slim { justify-content: center; padding: 10px; }
.gnm-user-logout {
  margin-left: auto; flex-shrink: 0;
  width: 28px; height: 28px; border-radius: 6px; border: none;
  background: transparent; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: #a09e98; transition: background .15s, color .15s;
}
.gnm-user-logout:hover { background: rgba(224,90,58,.1); color: #e05a3a; }
.gnm-user-logout svg { width: 14px; height: 14px; stroke: currentColor; }
.gnm-av {
  width: 34px; height: 34px; border-radius: 50%;
  background: #7c5cfc;
  color: #fff; font-size: 11px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; position: relative; letter-spacing: -.3px;
}
.gnm-av-dot {
  position: absolute; bottom: 0; right: 0;
  width: 9px; height: 9px; border-radius: 50%;
  border: 2px solid #fff;
  background: #a09e98;
  transition: background .2s;
}
.gnm-av-dot.on { background: #22c55e; }
.gnm-uinfo { flex: 1; min-width: 0; }
.gnm-uname {
  font-size: 12px; font-weight: 700; color: #1a1a1a;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1.3;
}
.gnm-urole {
  font-size: 10px; color: #a09e98; font-weight: 500; margin-top: 1px;
}
.gnm-online-btn {
  width: 30px; height: 17px; border-radius: 9px;
  background: #e4e3df; border: none; cursor: pointer;
  position: relative; flex-shrink: 0; transition: background .2s;
  padding: 0;
}
.gnm-online-btn.on { background: #22c55e; }
.gnm-online-knob {
  width: 11px; height: 11px; border-radius: 50%; background: #fff;
  position: absolute; top: 3px; left: 3px;
  transition: transform .2s;
  box-shadow: 0 1px 3px rgba(0,0,0,.2);
}
.gnm-online-btn.on .gnm-online-knob { transform: translateX(13px); }

/* ── Timezone bar ── */
.gnm-tz-bar {
  display: flex; align-items: center;
  padding: 7px 14px; flex-shrink: 0;
  border-bottom: 1px solid #eeedea;
  background: #fafaf9;
  gap: 0;
}
.gnm-tz-cell {
  display: flex; align-items: center; gap: 5px; flex: 1; justify-content: center;
}
.gnm-tz-flag { font-size: 11px; line-height: 1; }
.gnm-tz-time {
  font-size: 11px; font-weight: 600; color: #1a1a1a;
  font-family: 'JetBrains Mono', monospace; letter-spacing: .3px;
}
.gnm-tz-sep { width: 1px; height: 14px; background: #e4e3df; flex-shrink: 0; }

/* ── Scroll area ── */
.gnm-scroll {
  flex: 1; overflow-y: auto; overflow-x: hidden;
  padding: 6px 0 12px;
  scrollbar-width: thin;
  scrollbar-color: #e4e3df transparent;
}
.gnm-scroll::-webkit-scrollbar { width: 3px; }
.gnm-scroll::-webkit-scrollbar-thumb { background: #e4e3df; border-radius: 2px; }

/* ── Section labels ── */
.gnm-sec {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 14px 5px;
}
.gnm-sec-lbl {
  font-size: 9px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .8px; color: #c0bdb8; white-space: nowrap;
}
.gnm-sec-line {
  flex: 1; height: 1px; background: #eeedea;
}

/* ── Nav items ── */
.gnm-item {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 12px 8px 14px;
  margin: 1px 6px;
  border-radius: 8px; cursor: pointer;
  color: #6b6963; font-size: 12px; font-weight: 600;
  text-decoration: none; position: relative;
  transition: background .12s, color .12s;
  white-space: nowrap;
}
.gnm-item:hover { background: rgba(0,0,0,.04); color: #1a1a1a; }

/* Active state */
.gnm-item--active {
  background: rgba(124,92,252,.09) !important;
  color: #7c5cfc !important;
}
.gnm-item--active .gnm-ico svg { stroke: #7c5cfc; }

/* Active left pill */
.gnm-pill {
  position: absolute; left: -6px; top: 50%; transform: translateY(-50%);
  width: 3px; height: 18px; border-radius: 0 3px 3px 0;
  background: #7c5cfc;
}

/* Color accents on hover per section */
.gnm-c-dp:hover { background: rgba(124,92,252,.07); color: #7c5cfc; }
.gnm-c-sc:hover { background: rgba(232,137,28,.07); color: #e8891c; }
.gnm-c-rf:hover { background: rgba(199,84,80,.07); color: #c75450; }
.gnm-c-bn:hover { background: rgba(122,112,103,.07); color: #7a7067; }

.gnm-ico {
  width: 18px; height: 18px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.gnm-ico svg { width: 16px; height: 16px; stroke: currentColor; }
.gnm-lbl { flex: 1; }
.gnm-badge {
  background: #7c5cfc; color: #fff;
  font-size: 9px; font-weight: 700;
  padding: 2px 6px; border-radius: 20px; line-height: 1.2;
  flex-shrink: 0;
}

/* Collapsed mode */
.gnm-nav--collapsed .gnm-item {
  justify-content: center;
  padding: 10px;
  margin: 2px 6px;
}
.gnm-nav--collapsed .gnm-sec { display: none; }
.gnm-nav--collapsed .gnm-pill { display: none; }
.gnm-nav--collapsed .gnm-item--active {
  background: rgba(124,92,252,.1) !important;
}

/* ── Divider ── */
.gnm-hr { height: 1px; background: #eeedea; margin: 8px 12px; }

/* ── Logout ── */
.gnm-item--logout { color: #e05a3a !important; }
.gnm-item--logout:hover { background: rgba(224,90,58,.07) !important; color: #e05a3a !important; }

/* ── Mobile close ── */
.gnm-close-btn {
  color: #a09e98 !important;
  border-top: 1px solid #eeedea;
  border-radius: 0 !important;
  margin: 6px 0 0 !important;
  padding: 10px 14px !important;
}
.gnm-close-btn:hover { background: rgba(0,0,0,.04) !important; color: #1a1a1a !important; }
@media (min-width: 768px) { .gnm-close-btn { display: none; } }

/* ── Tooltip for collapsed icons ── */
.gnm-nav--collapsed .gnm-item[title]:hover::after {
  content: attr(title);
  position: absolute; left: calc(100% + 8px); top: 50%; transform: translateY(-50%);
  background: #1a1a1a; color: #fff;
  font-size: 11px; font-weight: 600;
  padding: 5px 10px; border-radius: 6px;
  white-space: nowrap; z-index: 999;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0,0,0,.2);
}
</style>
